// Typed API client for Katara PMS.

export type BedType = 'SINGLE' | 'QUEEN' | 'KING' | 'TWIN' | 'DOUBLE' | 'SOFA';
export type HousekeepingStatus = 'CLEAN' | 'DIRTY';
export type StayStatus = 'TENTATIVE' | 'CONFIRMED' | 'CHECKED_IN' | 'CHECKED_OUT' | 'NO_SHOW' | 'CANCELLED';
export type RateType = 'NIGHTLY' | 'WEEKLY';
export type BlockType = 'MAINTENANCE' | 'DEEP_CLEAN' | 'HOLD' | 'OUT_OF_ORDER' | 'PRIVATE';

export interface BlockInfo {
  id: string; blockType: string; reason: string;
  startsAt: string; endsAt: string;
}

export interface CurrentStay {
  id: string; status: StayStatus;
  arrivalDate: string; departureDate: string;
  occupantName: string; occupantProfileId: string;
}

export interface Bed {
  id: string; label: string; bedType: BedType;
  isBlocked: boolean;
  activeBlock: BlockInfo | null;
  upcomingBlock: BlockInfo | null;
  currentStay: CurrentStay | null;
}

export interface Room {
  id: string; number: string; displayName: string | null;
  housekeepingStatus: HousekeepingStatus;
  isOutOfOrder: boolean; outOfOrderReason: string | null;
  /** Room Tracker's ordering. Null means not placed; Room Plan ignores it. */
  trackerSortOrder: number | null;
  /** A provider can sleep here. Room Plan and stays filter on this. */
  supportsAccommodation: boolean;
  /** A provider can see a client here. Room Tracker filters on this. */
  supportsBookings: boolean;
  beds: Bed[];
}

export interface Property {
  id: string;
  name: string;
  code: string;
  timezone: string;
  entity: { id: string; name: string };
  serverTime: string;
}

export type ChargeType =
  | 'ROOM' | 'CLEANING' | 'DAMAGE' | 'EXTRA' | 'DISCOUNT' | 'WAIVER'
  | 'REFUND' | 'OTHER' | 'KEY_BOND' | 'BOND_RETURN' | 'KEY_REPLACEMENT';

export interface FolioCharge {
  id: string;
  chargeType: ChargeType;
  description: string;
  amount: string;        // Decimal as string
  postedAt: string;
  postedBy: string;
  isVoid: boolean;
  voidedAt: string | null;
  voidedBy: string | null;
  voidedReason: string | null;
  waivedReason: string | null;
  relatedKey: { keyLabel: string } | null;
}

export type PaymentMethod = 'CASH' | 'EFTPOS' | 'BANK_TRANSFER' | 'OTHER';

export interface PaymentItem {
  id: string;
  paymentType: 'PREAUTH' | 'CAPTURE' | 'CHARGE' | 'REFUND' | 'VOID';
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'EXPIRED';
  amount: string;
  processor: string;
  processorRef: string | null;
  notes: string | null;
  failureReason: string | null;
  authExpiresAt: string | null;
  capturedAt: string | null;
  createdAt: string;
  createdBy: string;
  parentPaymentId: string | null;
}

export interface FolioPayload {
  charges: FolioCharge[];
  payments: PaymentItem[];
  totals: { charged: string; voided: string; paid: string; preauthHeld: string; balance: string };
}

export interface StayDetail {
  id: string;
  status: StayStatus;
  arrivalDate: string; departureDate: string;
  actualCheckIn: string | null; actualCheckOut: string | null;
  rateType: RateType;
  nightlyRate: string;
  weeklyRate: string | null;
  discountAmount: string;
  discountReason: string | null;
  notes: string | null;
  nights: number;
  createdAt: string; createdBy: string; updatedAt: string;
  occupant: { id: string; workingName: string; phone: string | null; notes: string | null };
  bed: { id: string; label: string; bedType: BedType };
  room: { id: string; number: string; displayName: string | null; housekeepingStatus: HousekeepingStatus };
  folio?: FolioPayload;
}

export interface StayListItem {
  id: string;
  status: StayStatus;
  arrivalDate: string; departureDate: string;
  actualCheckIn: string | null; actualCheckOut: string | null;
  rateType: RateType;
  nightlyRate: string;
  occupantName: string;
  occupantPhone: string | null;
  bedLabel: string;
  roomNumber: string;
  roomDisplayName: string | null;
}

export interface StaySummary {
  arrivals: number;
  inhouse: number;
  departures: number;
  upcoming: number;
}

export interface BlockDetail {
  id: string;
  blockType: string;
  reason: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  releasedAt: string | null;
  releasedBy: string | null;
  releasedReason: string | null;
  bed: { id: string; label: string; bedType: string };
  room: { id: string; number: string; displayName: string | null };
}

export type KeyType = 'ROOM' | 'OTHER';

export interface KeyActiveIssue {
  id: string;
  issuedAt: string;
  issuedBy: string;
  bondAmount: string | null;  // Decimal as string from API
  notes: string | null;
  profile: { id: string; workingName: string };
  stay: {
    id: string;
    arrivalDate: string;
    departureDate: string;
    bed: {
      label: string;
      room: { id: string; number: string; displayName: string | null } | null;
    } | null;
  };
}

export interface KeyItem {
  id: string;
  label: string;
  keyType: KeyType;
  isLost: boolean;
  notes: string | null;
  room: { id: string; number: string; displayName: string | null } | null;
  activeIssue: KeyActiveIssue | null;
}

export interface ProfileListItem {
  id: string;
  workingName: string;
  phone: string | null;
  email: string | null;
  dateOfBirth: string | null;
  isArchived: boolean;
  archivedAt: string | null;
  archivedReason: string | null;
  notes: string | null;
  aliases: string[];
  stayCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileAlias {
  id: string;
  alias: string;
  notes: string | null;
}

export interface ProfileStay {
  id: string;
  status: string;
  arrivalDate: string;
  departureDate: string;
  actualCheckIn: string | null;
  actualCheckOut: string | null;
  rateType: string;
  nightlyRate: number;
  bedLabel: string;
  roomNumber: string;
  roomDisplayName: string | null;
}

/** A staff observation. Immutable — there is no edit path by design. */
export interface ProfileNote {
  id: string;
  body: string;
  createdAt: string;
  /** Display form, e.g. "Jess (Recep)". */
  author: string;
  authorRole: string;
}

export interface StayNote {
  id: string;
  status: string;
  arrivalDate: string;
  departureDate: string;
  notes: string | null;
  /** False when she was a listed occupant rather than the booking's primary. */
  wasPrimary: boolean;
  roomLabel: string;
  bedLabel: string;
}

export interface ReservationDeclarationNote {
  id: string;
  date: string;
  mode: string;
  status: string;
  shiftTimes: string | null;
  approvalNotes: string | null;
  cancelNotes: string | null;
  cancelReason: string | null;
  unavailableReason: string | null;
  rejectedReason: string | null;
  slotNotes: string | null;
  approvedBy: string | null;
  cancelledBy: string | null;
}

export interface ReservationHistoryEvent {
  id: string;
  date: string;
  fromStatus: string;
  toStatus: string;
  reason: string | null;
  notes: string | null;
  performedAt: string;
  /** Display form, e.g. "Aaron (Admin)". */
  performedBy: string;
}

export interface ProfileDetail {
  id: string;
  workingName: string;
  phone: string | null;
  email: string | null;
  dateOfBirth: string | null;
  notes: string | null;
  isActive: boolean;
  isArchived: boolean;
  archivedAt: string | null;
  archivedReason: string | null;
  archivedBy: string | null;
  unarchivedAt: string | null;
  unarchivedBy: string | null;
  unarchiveReason: string | null;
  createdAt: string;
  updatedAt: string;
  aliases: ProfileAlias[];
  stats: {
    totalStays: number;
    totalNights: number;
    lastRoom: string | null;
    lastVisit: string | null;
    lastRate: number | null;
  };
  stays: ProfileStay[];

  // Portal access state (Availability module)
  hasPortalAccess: boolean;
  username: string | null;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  assignedPropertyId: string | null;
  notifyVia: 'SMS' | 'EMAIL' | 'BOTH';
}

export interface HousekeepingLogEntry {
  id: string;
  previousStatus: 'CLEAN' | 'DIRTY' | null;
  newStatus: 'CLEAN' | 'DIRTY';
  note: string | null;
  performedBy: string;
  performedAt: string;
}

export interface HousekeepingLogResponse {
  room: { id: string; number: string; displayName: string | null };
  logs: HousekeepingLogEntry[];
}

// --- Room Tracker -----------------------------------------------------------

/**
 * A live booking: one client with one provider in one room. Note what is
 * absent: there is no status field. The
 * server sends startedAt, durationMinutes and a derived expiresAt, and the
 * countdown is computed on the client — the board is polled every couple of
 * seconds, so any state the server had computed would already be stale by the
 * time it rendered.
 */
export interface BookingItem {
  id: string;
  roomId: string;
  roomNumber: string;
  roomDisplayName: string | null;
  profileId: string;
  workingName: string;
  startedAt: string;
  durationMinutes: number;
  expiresAt: string;
  startedBy: string;
  endedAt: string | null;
  endedBy: string | null;
  endReason: string | null;
}

export interface BookingBoard {
  /** Authoritative time, used to correct for a terminal whose clock is wrong. */
  serverNow: string;
  /** Minutes before expiry at which a booking turns yellow. */
  warningMinutes: number;
  bookings: BookingItem[];
}

/** A cleaning note left on a room, optionally raised from a booking. */
export interface CleaningNote {
  id: string;
  roomId: string;
  roomNumber: string;
  roomDisplayName: string | null;
  body: string;
  bookingId: string | null;
  bookingProvider: string | null;
  bookingStartedAt: string | null;
  createdBy: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

/**
 * Booking counts for one business day, grouped by room. Display only — there is
 * deliberately no threshold or flag attached, because providers clean their own
 * rooms after each client and this feeds deep-clean scheduling, not turnover.
 */
export interface BookingCounts {
  /** The business day these counts cover, YYYY-MM-DD in property-local terms. */
  date: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  businessDayStartHour: number;
  counts: { roomId: string; count: number }[];
}

/**
 * Error carrying the server's HTTP status and optional machine-readable code.
 * Extends Error, so existing `e instanceof Error ? e.message` call sites are
 * unaffected; callers that need to branch (e.g. assign's PAST_DATE and
 * DECLARATION_EXISTS confirmations) can read `.code`.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(
      data.error || `${url} → HTTP ${res.status}`,
      res.status,
      typeof data.code === 'string' ? data.code : undefined,
    );
  }
  return res.json() as Promise<T>;
}

export const api = {
  property: () => fetchJson<Property>('/api/property'),
  rooms:    () => fetchJson<Room[]>('/api/rooms'),

  stay: (id: string) => fetchJson<StayDetail>(`/api/stays/${id}`),

  checkIn: (id: string) =>
    fetchJson<{ id: string; status: StayStatus; actualCheckIn: string }>(
      `/api/stays/${id}/check-in`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: '{}' }
    ),

  checkOut: (id: string) =>
    fetchJson<{ id: string; status: StayStatus; actualCheckOut: string }>(
      `/api/stays/${id}/check-out`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: '{}' }
    ),

  cancel: (id: string, reason: string) =>
    fetchJson<{ id: string; status: StayStatus }>(
      `/api/stays/${id}/cancel`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }
    ),

  editStay: (id: string, patch: {
    arrivalDate?: string; departureDate?: string;
    rateType?: RateType; nightlyRate?: number;
    weeklyRate?: number | null; notes?: string | null;
  }) =>
    fetchJson<{ id: string; status: StayStatus }>(
      `/api/stays/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }
    ),

  staysList: (view: 'arrivals' | 'inhouse' | 'departures' | 'upcoming' | 'all') =>
    fetchJson<StayListItem[]>(`/api/stays?view=${view}`),

  staysSummary: () => fetchJson<StaySummary>('/api/stays/summary'),

  createBlock: (body: {
    bedId: string;
    blockType: 'MAINTENANCE' | 'DEEP_CLEAN' | 'HOLD' | 'OUT_OF_ORDER';
    reason: string;
    startsAt: string;
    endsAt: string;
  }) =>
    fetchJson<{ id: string; blockType: string; startsAt: string; endsAt: string }>(
      '/api/blocks',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  releaseBlock: (id: string, reason?: string) =>
    fetchJson<{ id: string; isActive: boolean; endsAt: string }>(
      `/api/blocks/${id}/release`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason || '' }) }
    ),

  setRoomHousekeeping: (id: string, newStatus: 'CLEAN' | 'DIRTY', note?: string) =>
    fetchJson<{ id: string; housekeepingStatus: string; housekeepingUpdatedAt: string }>(
      `/api/rooms/${id}/housekeeping`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newStatus, note: note || '' }) }
    ),

  /** Writes Room Tracker's room order. Send every room id, in order. */
  setTrackerRoomOrder: (order: string[]) =>
    fetchJson<{ rooms: { id: string; number: string; displayName: string | null; trackerSortOrder: number | null }[] }>(
      '/api/rooms/tracker-order',
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ order }) },
    ),

  housekeepingLog: (roomId: string) =>
    fetchJson<HousekeepingLogResponse>(`/api/rooms/${roomId}/housekeeping-log`),

  // --- Room Tracker ---

  bookings: () => fetchJson<BookingBoard>('/api/bookings'),

  startBooking: (body: { roomId: string; profileId: string; durationMinutes: number }) =>
    fetchJson<BookingItem>('/api/bookings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  extendBooking: (id: string, addMinutes: number) =>
    fetchJson<BookingItem>(`/api/bookings/${id}/extend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addMinutes }),
    }),

  endBooking: (id: string, reason?: string) =>
    fetchJson<BookingItem>(`/api/bookings/${id}/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || '' }),
    }),

  // --- Housekeeping tie-in ---

  /** Omit `date` for the previous business day — the night just finished. */
  bookingCounts: (date?: string) =>
    fetchJson<BookingCounts>(`/api/rooms/booking-counts${date ? `?date=${encodeURIComponent(date)}` : ''}`),

  cleaningNotes: (includeResolved = false) =>
    fetchJson<{ notes: CleaningNote[] }>(`/api/cleaning-notes?includeResolved=${includeResolved}`),

  roomCleaningNotes: (roomId: string) =>
    fetchJson<{ notes: CleaningNote[] }>(`/api/rooms/${roomId}/cleaning-notes`),

  addCleaningNote: (roomId: string, body: string, bookingId?: string) =>
    fetchJson<CleaningNote>(`/api/rooms/${roomId}/cleaning-notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body, bookingId: bookingId || undefined }),
    }),

  resolveCleaningNote: (id: string) =>
    fetchJson<CleaningNote>(`/api/cleaning-notes/${id}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  deleteCleaningNote: (id: string, reason: string) =>
    fetchJson<{ id: string; deleted: boolean }>(`/api/cleaning-notes/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }),

  blockDetail: (id: string) => fetchJson<BlockDetail>(`/api/blocks/${id}`),

  profiles: (q?: string, includeArchived = false) =>
    fetchJson<ProfileListItem[]>(
      `/api/profiles?q=${encodeURIComponent(q || '')}&includeArchived=${includeArchived}`
    ),

  profileDetail: (id: string) => fetchJson<ProfileDetail>(`/api/profiles/${id}`),

  // --- Profile notes (staff-facing only; never exposed to the portal) ---

  profileNotes: (profileId: string) =>
    fetchJson<{ notes: ProfileNote[] }>(`/api/profiles/${profileId}/notes`),

  addProfileNote: (profileId: string, body: string) =>
    fetchJson<ProfileNote>(`/api/profiles/${profileId}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    }),

  /** Soft delete, MANAGER+. Reason is required by the server. */
  deleteProfileNote: (profileId: string, noteId: string, reason: string) =>
    fetchJson<{ id: string; deleted: boolean }>(
      `/api/profiles/${profileId}/notes/${noteId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    ),

  /** Permanent erasure, ADMIN only. Keeps a purge record, not the text. */
  purgeProfileNote: (profileId: string, noteId: string, reason: string) =>
    fetchJson<{ id: string; purged: boolean }>(
      `/api/profiles/${profileId}/notes/${noteId}/purge`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      },
    ),

  profileReservationNotes: (profileId: string) =>
    fetchJson<{ stays: StayNote[] }>(`/api/profiles/${profileId}/stay-notes`),

  profileShiftNotes: (profileId: string) =>
    fetchJson<{ declarations: ReservationDeclarationNote[]; events: ReservationHistoryEvent[] }>(
      `/api/profiles/${profileId}/reservation-notes`,
    ),

  updateProfile: (id: string, patch: {
    workingName?: string;
    phone?: string | null;
    email?: string | null;
    dateOfBirth?: string | null;
    notes?: string | null;
  }) =>
    fetchJson<{ id: string; workingName: string }>(
      `/api/profiles/${id}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }
    ),

  archiveProfile: (id: string, reason: string) =>
    fetchJson<{ id: string; isArchived: boolean; archivedAt: string; archivedBy: string | null }>(
      `/api/profiles/${id}/archive`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }
    ),

  /** Restores an archived profile. MANAGER+, and the archive trail is kept. */
  unarchiveProfile: (id: string, reason: string) =>
    fetchJson<{ id: string; isArchived: boolean; unarchivedAt: string; unarchivedBy: string | null }>(
      `/api/profiles/${id}/unarchive`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }
    ),

  addAlias: (profileId: string, alias: string, notes?: string) =>
    fetchJson<{ id: string; alias: string }>(
      `/api/profiles/${profileId}/aliases`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ alias, notes: notes || '' }) }
    ),

  deleteAlias: (profileId: string, aliasId: string) =>
    fetchJson<{ ok: boolean }>(
      `/api/profiles/${profileId}/aliases/${aliasId}`,
      { method: 'DELETE' }
    ),

  // Creates the profile only. propertyId comes from the session's current
  // property server-side — it is deliberately not a parameter here.
  createProfile: (body: {
    workingName: string;
    phone?: string | null;
    email?: string | null;
    dateOfBirth?: string | null;
    notes?: string | null;
  }) =>
    fetchJson<{
      id: string;
      workingName: string;
      email: string | null;
      phone: string | null;
      propertyId: string;
    }>(
      '/api/profiles',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  // --- Portal access (Availability module) ---
  grantPortalAccess: (profileId: string) =>
    fetchJson<{
      profile: {
        id: string;
        workingName: string;
        email: string;
        phone: string;
        username: string;
        assignedPropertyId: string | null;
      };
      tempPassword: string;
      isReset: boolean;
    }>(
      `/api/profiles/${profileId}/grant-portal-access`,
      { method: 'POST' }
    ),

  // --- Keys ---
  listKeys: () => fetchJson<KeyItem[]>('/api/keys'),

  createKey: (body: { label: string; keyType?: KeyType; roomId?: string | null; notes?: string | null }) =>
    fetchJson<{ id: string; label: string }>(
      '/api/keys',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  issueKey: (keyId: string, body: { stayId: string; profileId?: string; bondAmount?: number | null; notes?: string | null }) =>
    fetchJson<{ id: string; issuedAt: string; bondAmount: string | null }>(
      `/api/keys/${keyId}/issue`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  returnKey: (keyId: string, notes?: string | null) =>
    fetchJson<{ id: string; returnedAt: string }>(
      `/api/keys/${keyId}/return`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: notes || null }) }
    ),

  loseKey: (keyId: string, reason: string) =>
    fetchJson<{ id: string; isLost: boolean }>(
      `/api/keys/${keyId}/lose`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }
    ),

  archiveKey: (keyId: string) =>
    fetchJson<{ id: string; isArchived: boolean }>(
      `/api/keys/${keyId}/archive`,
      { method: 'PATCH' }
    ),

  // --- Folio ---
  addCharge: (stayId: string, body: { chargeType: ChargeType; description: string; amount: number; waivedReason?: string }) =>
    fetchJson<{ id: string; amount: string }>(
      `/api/stays/${stayId}/charges`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  voidCharge: (chargeId: string, reason: string) =>
    fetchJson<{ id: string; isVoid: boolean }>(
      `/api/folio-charges/${chargeId}/void`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }
    ),

  // --- Payments ---
  recordCharge: (stayId: string, body: { amount: number; processor: PaymentMethod; processorRef?: string; notes?: string }) =>
    fetchJson<{ id: string; paymentType: string; amount: string }>(
      `/api/stays/${stayId}/payments/charge`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  recordRefund: (stayId: string, body: { amount: number; processor: PaymentMethod; processorRef?: string; notes?: string }) =>
    fetchJson<{ id: string; paymentType: string; amount: string }>(
      `/api/stays/${stayId}/payments/refund`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  recordPreauth: (stayId: string, body: { amount: number; processor?: PaymentMethod; processorRef?: string; notes?: string }) =>
    fetchJson<{ id: string; paymentType: string; amount: string; authExpiresAt: string }>(
      `/api/stays/${stayId}/payments/preauth`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    ),

  voidPayment: (paymentId: string, reason: string) =>
    fetchJson<{ id: string; status: string; action: string }>(
      `/api/payments/${paymentId}/void`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }
    ),
};

// ===========================================================================
// Staff-side Availability endpoints (Phase 2C)
// ===========================================================================

export type DayPart = 'DAY' | 'NIGHT' | 'GRAVEYARD';

/** A declaration holding one spot. A spot has at most one. */
export interface ReservationAssignment {
  id: string;
  profileId: string;
  workingName: string;
  status: 'CONFIRMED' | 'PENDING_OVERFLOW' | 'COMPLETED' | 'NO_SHOW';
}

/**
 * One spot: a single opportunity for one provider, with its own times.
 *
 * There is no `capacity`, no `bookedCount` and no `isFull`. Six openings on a
 * Friday are six of these, each editable on its own — which is why a spot has
 * a single `holder` rather than a list of assignments.
 */
export interface StaffReservation {
  id: string;
  date: string;          // YYYY-MM-DD
  section: DayPart;
  startTime: string;     // HH:MM
  endTime: string;       // HH:MM
  notes: string | null;
  isCancelled: boolean;
  /** Who holds this spot, or null if it is open. */
  holder: ReservationAssignment | null;
  createdAt: string;
  createdBy: string;
}

export interface PendingDeclaration {
  id: string;
  date: string;
  mode: 'RESERVED' | 'CUSTOM' | 'ON_CALL' | 'UNAVAILABLE';
  status: 'PENDING_APPROVAL' | 'PENDING_OVERFLOW';
  profile: {
    id: string;
    workingName: string;
    phone: string | null;
    email: string | null;
  };
  /** The spot she holds, or null — PENDING_OVERFLOW means no spot yet. */
  reservation: {
    id: string;
    startTime: string;
    endTime: string;
    isCancelled: boolean;
  } | null;
  customStart: string | null;
  customEnd: string | null;
  unavailableReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A confirmed declaration not attached to a shift slot — custom hours, or an
 * on-call offer. Rendered as its own card on the week grid.
 */
export type CallOffReason =
  | 'SICK'
  | 'PERSONAL_EMERGENCY'
  | 'STAFF_CANCELLED'
  | 'TRANSPORT_FAILURE'
  | 'OTHER';

export interface WeekDeclaration {
  id: string;
  date: string;
  mode: 'CUSTOM' | 'ON_CALL' | 'RESERVED' | 'UNAVAILABLE';
  /** CONFIRMED for custom/on-call cards; CALLED_OFF for the called-off row. */
  status: 'CONFIRMED' | 'CALLED_OFF';
  /** Manager's choice at approval. Null on legacy rows — grid derives one. */
  section: DayPart | null;
  customStart: string | null;
  customEnd: string | null;
  /** Times of the shift she called off, when the row came from a slot. */
  shiftStart: string | null;
  shiftEnd: string | null;
  cancelReason: CallOffReason | null;
  cancelNotes: string | null;
  /** Who approved it, in "Matthew (Mgr)" form. Null when never approved. */
  approvedBy: string | null;
  approvedAt: string | null;
  profileId: string;
  workingName: string;
  phone: string | null;
  createdAt: string;
}

export const staffAvailabilityApi = {
  listReservations: (week: string) =>
    fetchJson<{ weekStart: string; weekEnd: string; reservations: StaffReservation[] }>(
      `/api/staff/reservations?week=${encodeURIComponent(week)}`,
    ),

  listWeekDeclarations: (week: string) =>
    fetchJson<{ weekStart: string; weekEnd: string; declarations: WeekDeclaration[] }>(
      `/api/staff/availability/week?week=${encodeURIComponent(week)}`,
    ),

  assignProvider: (reservationId: string, body: {
    profileId: string;
    replace?: boolean;
    confirmPast?: boolean;
  }) =>
    fetchJson<StaffReservation>(`/api/staff/reservations/${encodeURIComponent(reservationId)}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /** Publishes `count` independent spots in one transaction. */
  createReservation: (body: {
    date: string;
    section: DayPart;
    startTime: string;
    endTime: string;
    count: number;
    notes?: string;
  }) =>
    fetchJson<{ created: number; spots: StaffReservation[] }>('/api/staff/reservations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  patchReservation: (id: string, body: {
    section?: DayPart;
    startTime?: string;
    endTime?: string;
    notes?: string | null;
  }) =>
    fetchJson<StaffReservation>(`/api/staff/reservations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  cancelReservation: (id: string, reason?: string) =>
    fetchJson<unknown>(`/api/staff/reservations/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || undefined }),
    }),

  listPending: () =>
    fetchJson<{ pending: PendingDeclaration[] }>('/api/staff/availability/pending'),

  /** Rostered provider rang ahead and pulled out. Reason is optional. */
  callOff: (declarationId: string, body?: { reason?: CallOffReason; notes?: string }) =>
    fetchJson<{ id: string; status: string }>(
      `/api/staff/availability/${encodeURIComponent(declarationId)}/call-off`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: body?.reason || undefined, notes: body?.notes || undefined }),
      },
    ),

  /**
   * Reverses a call-off recorded in error. 409 SHIFT_FULL if refilled.
   *
   * Sends an explicit empty object: declaring a JSON content-type with no
   * body makes Fastify reject the request as FST_ERR_CTP_EMPTY_JSON_BODY
   * before the handler ever runs.
   */
  reinstate: (declarationId: string) =>
    fetchJson<{ id: string; status: string }>(
      `/api/staff/availability/${encodeURIComponent(declarationId)}/reinstate`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    ),

  /** section is required for CUSTOM declarations, rejected for other modes. */
  approveDeclaration: (id: string, opts?: { notes?: string; section?: DayPart }) =>
    fetchJson<unknown>(`/api/staff/availability/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        notes: opts?.notes || undefined,
        section: opts?.section || undefined,
      }),
    }),

  rejectDeclaration: (id: string, rejectedReason: string) =>
    fetchJson<unknown>(`/api/staff/availability/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rejectedReason }),
    }),
};
